import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useMemo,
} from 'react';
import api from '../api';
import Button from 'react-bootstrap/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { toast } from 'react-toastify';
import { Store } from '../Store';
import { getError, formatEuro, formatDate } from '../utils';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {
  Container,
  Badge,
  Nav,
  Form,
  Modal,
  Dropdown,
  DropdownButton,
} from 'react-bootstrap';
import Pagination from '../components/general/Pagination';
import {
  buildRefundLines,
  calculateRefundAmount,
  buildRefundRequestItems,
  normalizeRefundValue,
  initialRefundLine,
  refundLineWithPatch,
} from '../utils/refundForm';
import {
  orderListFilter,
  orderActions,
  filmInputDefaults,
  missingCardboardItems,
  missingFoamItems,
  selectedFreeStock,
  packagingQuantityFromInput,
  extraPackagingQuantityFromInput,
  usedFilmFromInput,
  shipmentAdjustments,
  orderStatusBody,
  refundExceedsOrderTotal,
  canSubmitRefund,
} from '../utils/orderList';
import { freeStock } from '../utils/stockAdjustments';
import { totalAfterRefund } from '../utils/orderTotals';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        orders: action.payload.orders,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function OrderListScreen() {
  const [{ loading, error, orders, pages }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const navigate = useNavigate();

  const { state } = useContext(Store);
  const { userInfo } = state;

  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);

  const [pendingOrders, setPendingOrders] = useState(0);
  const [processingOrders, setProcessingOrders] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0); // State to trigger refresh

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present
  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input
  const filterParam = orderListFilter(sp.get('filter'), pendingOrders);

  const [showShipModal, setShowShipModal] = useState(false);
  const [shipOrderId, setShipOrderId] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const SHIPPING_PROVIDERS = ['DHL', 'UPS', 'DPD', 'FedEx'];
  const [shippingProvider, setShippingProvider] = useState('DHL');
  const [filmRows, setFilmRows] = useState([]); // stores the reserved transferFilm quantity for orderItems that reference a Layout that has allowFilmPooling enabled [{orderItemId, title, reservedQty}]
  const [filmInputs, setFilmInputs] = useState({}); // stores the user inputs of used transferFilm quantity per orderItem (only for orderItems that reference a Layout that has allowFilmPooling enabled) { [orderItemId]: usedQty }
  const [shippingBoxes, setShippingBoxes] = useState([]); // stores all the shipping boxes from the db ([{_id, name/size, stockQuantity, reservedQuantity}])
  const [keycapPullers, setKeycapPullers] = useState([]); // stores all the keycap pullers from the db ([{_id, name, stockQuantity, reservedQuantity}])
  const [manualShipBoxes, setManualShipBoxes] = useState([]); // stores the shipping boxes that were used for this order
  const [manualPullers, setManualPullers] = useState([]); // stores the keycap pullers that were used for this order
  const [cardboardBoxes, setCardboardBoxes] = useState([]); // stores all the cardboard boxes from the db
  const [foams, setFoams] = useState([]); // stores all the foams from the db
  const [extraCardboardRows, setExtraCardboardRows] = useState([]); // stores the cardboard boxes that were used for keycap sets in this order, that are based on layouts that have no cardboard box referenced
  const [extraFoamRows, setExtraFoamRows] = useState([]); // stores the foams that were used for keycap sets in this order, that are based on layouts that have no foam referenced
  const [packagingGapItems, setPackagingGapItems] = useState([]); // order items whose layouts do not reference a cardboardBox and/or foam

  const [refundOrderId, setRefundOrderId] = useState(null); // the orderID of the order to be refunded
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null); // the full order object of the order to be refunded
  const [maxRefund, setMaxRefund] = useState(0); // stores the order.totalGross of the order to be refunded, to make sure the refund total doesn't exceed the totalGross of the original order
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundItems, setRefundItems] = useState({}); // stores the configs of all refund items

  const openShipModal = async (orderId) => {
    setShipOrderId(orderId);
    setTrackingNumber('');
    setShippingProvider('DHL'); // reset

    // reset all film / extra-usage state
    setFilmRows([]);
    setFilmInputs({});
    setManualShipBoxes([]);
    setManualPullers([]);
    setExtraCardboardRows([]);
    setExtraFoamRows([]);
    setPackagingGapItems([]);

    setShowShipModal(true);

    try {
      const { data } = await api.get(`/api/orders/${orderId}/film-usage`); // fetch the reserved transferFilm quantity of all orderItems that reference a Layout that has allowFilmPooling enabled (data.rows = [{ orderItemId, title, reservedQty }])
      setFilmRows(data.rows); // the route always sends rows and packagingGaps.items, so their fallbacks went (Oct 2026, user decision); a failed request lands in the catch below
      setFilmInputs(filmInputDefaults(data.rows));
      setPackagingGapItems(data.packagingGaps.items); // store packaging gap items
    } catch (e) {
      // no pooled items or fetch failed → keep empty, shipping can still proceed
      setFilmRows([]);
      setFilmInputs({});
      setPackagingGapItems([]);
    }

    try {
      // 2) fetch lists of shipping boxes, keycap pullers, cardboard boxes, foams
      const [sbRes, kpRes, cbRes, foamRes] = await Promise.all([
        api.get('/api/shipping-boxes/all'), // fetch all active shipping boxes
        api.get('/api/keycap-pullers/all'), // fetch all active keycap pullers
        api.get('/api/cardboard-boxes/all'), // fetch all active cardboard boxes
        api.get('/api/foam/all'), // fetch all active foams (lid & base)
      ]);

      setShippingBoxes(sbRes.data || []);
      setKeycapPullers(kpRes.data || []);
      setCardboardBoxes(cbRes.data || []);
      setFoams(foamRes.data || []);
    } catch (err) {
      console.error('Error fetching packing lists:', err);
    }
  }; // this function is triggered after the admin wants to update the status of an order to 'shipped'

  const itemsMissingCardboard = useMemo(
    () => missingCardboardItems(packagingGapItems),
    [packagingGapItems]
  ); // check if any orderItems of the order (for which the openShipModal function has been triggered) reference a Layout that does not reference any cardboardBox document

  const itemsMissingFoam = useMemo(
    () => missingFoamItems(packagingGapItems),
    [packagingGapItems]
  ); // check if any orderItems of the order (for which the openShipModal function has been triggered) reference a Layout that does not reference any foam document

  const closeShipModal = () => {
    setShipOrderId(null);
    setTrackingNumber('');
    setFilmRows([]);
    setFilmInputs({});
    setShowShipModal(false);
  }; // this function handles closing the modal

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        // Include the search parameter in the API request if it exists
        const { data } = await api.get(
          `/api/orders?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}&filter=${filterParam}` // encodeURIComponent: encodes the searchQuery to ensure that special characters are properly formatted in the URL, preventing errors and ensuring accurate query handling
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };

    const fetchPendingCount = async () => {
      try {
        const { data } = await api.get(
          '/api/orders/count/pendingAndProcessing'
        );
        setPendingOrders(data.pendingCount);
        setProcessingOrders(data.processingCount);
      } catch (error) {
        console.error('Error fetching pending orders count:', error);
      }
    };

    fetchData(); // fetches the order data from the backend
    fetchPendingCount();
  }, [page, userInfo, searchQuery, filterParam, refreshKey]); // a delete family (four actions, two flags, a reset branch here) that nothing dispatched was deleted Oct 2026

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.trim(); // Keep spaces between words but remove leading/trailing spaces
    navigate(
      sanitizedInput
        ? `/admin/orders?filter=${filterParam}&search=${encodeURIComponent(
            sanitizedInput
          )}&page=1`
        : `/admin/orders?filter=${filterParam}&page=1`
    ); // Navigates to the OrderListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  const updateOrderStatus = async (
    id,
    newStatus,
    { orderId, shipment } = {}
  ) => {
    try {
      setLoadingStatusUpdate(true);

      await api.put(
        `/api/orders/status/${id}`,
        orderStatusBody(newStatus, shipment)
      ); // the status alone, or with 'shipped' the dialog's fields; orderId names the process-order page to open (the unread '' default and the null the ship call passed to reach the fields went, Oct 2026, user decision)

      toast.success(`Order status updated to ${newStatus}`);

      if (newStatus === 'processing') {
        navigate(`/admin/process-order/${orderId}`);
      } else {
        setRefreshKey((prevKey) => prevKey + 1); // Increment refreshKey to trigger useEffect
      }
      return true;
    } catch (err) {
      toast.error(getError(err));
      return false; // the ship dialog reads this to stay open with its inputs after a refused shipment (it closed either way until Oct 2026, fixed on the spot)
    } finally {
      setLoadingStatusUpdate(false);
    }
  };

  const cancelOrder = (orderId) => {
    const confirmMessage = `
      Are you sure you want to cancel this order?
      
      All reserved materials for this order (Keycaps, Cardboard Boxes, Foams, and Transfer Film) will be unreserved and returned to stock.
      
      ⚠️ If any of these materials have already been manufactured or are no longer reusable for other orders, you must manually add them to the scrap in the stock.`;

    if (window.confirm(confirmMessage.trim())) {
      updateOrderStatus(orderId, 'cancelled');
    }
  };

  const openRefundModal = (order) => {
    setRefundOrderId(order._id);
    setRefundOrder(order);
    setMaxRefund(order.totalGross ?? 0); // stores the order.totalGross of the order to be refunded, to make sure the refund total doesn't exceed the totalGross of the original order (totalGross is required by the model, so its fallback covers nothing; the cap stays, as the refund type switch pushes the lines above the total until KNOWN_BUGS.md's refund-modal entry is fixed, user decision Oct 2026)
    setRefundItems({}); // reset modal config
    setRefundError('');
    setShowRefundModal(true); // open the modal
  };

  const closeRefundModal = () => {
    if (submittingRefund) return; // don't let the admin close the refund modal, when the refund is still submitting

    setShowRefundModal(false);
    setRefundOrderId(null);
    setRefundOrder(null);
  };

  const enableItem = (item) => {
    setRefundItems((prev) => ({
      ...prev,
      [item.key]: initialRefundLine(item),
    }));
  }; // this function adds an item to the refund

  const orderItemsOfRefundOrder = useMemo(
    () => buildRefundLines(refundOrder),
    [refundOrder]
  ); // computes the orderItemsOfRefundOrder array, which contains all items of the refundOrder (incl. shipping as a separate item)

  const setItemRefund = (key, patch) => {
    setRefundItems((prev) => ({
      ...prev,
      [key]: refundLineWithPatch(prev[key], patch),
    }));
  }; // helper function to edit an item that will be refunded

  const clearItemRefund = (key) => {
    setRefundItems((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  }; // removes an item from itemRefund (will be excluded from the refund)

  const refundAmount = useMemo(
    () => calculateRefundAmount(orderItemsOfRefundOrder, refundItems),
    [orderItemsOfRefundOrder, refundItems]
  ); // will compute the total refund amount (The calculation of refundAmount follows the exact same calculation of the refundGross in the backend. This ensures the preview matches the invoice.)

  const exceedsOrderTotal = refundExceedsOrderTotal(refundAmount, maxRefund); // make sure the total refund amount does not exceed the order.totalGross

  const canSubmit = useMemo(
    () =>
      canSubmitRefund({
        refundOrderId,
        refundAmount,
        maxRefund,
        refundItems,
        orderItemsOfRefundOrder,
      }),
    [
      refundOrderId,
      refundAmount,
      maxRefund,
      refundItems,
      orderItemsOfRefundOrder,
    ]
  ); // a quick check, if a refund can be submitted

  const submitRefund = async () => {
    // the opening guard (no order selected) and the cap re-check went (Oct 2026, user decision): the button that calls this is disabled while canSubmit is false, which covers both
    const confirmMessage = `Are you sure you want to refund this order?

This will generate the invoice correction and store the refund inside the order document.

The actual refund must be made manually in the PayPal dashboard.

It is best to check out the generated correction invoice for the refund, and refund the invoice total, instead of relying on the refund amount preview in this modal.`;

    if (!window.confirm(confirmMessage.trim())) return; // double check with the admin, if he really wants to refund

    setSubmittingRefund(true);
    setRefundError('');

    try {
      // configure the payload for the API request:
      const payload = {
        items: buildRefundRequestItems(orderItemsOfRefundOrder, refundItems),
      };

      await api.put(`/api/orders/refund/${refundOrderId}`, payload); // send the refund request to the backend

      closeRefundModal();
      toast.success(`Refund successful`);
      setRefreshKey((k) => k + 1); // refresh the screen
    } catch (err) {
      setRefundError(getError(err)); // the backend's message, else axios's, as the screen's other handlers read them (the chain this spelled out, with a fixed text behind it, went Oct 2026, user decision)
    } finally {
      setSubmittingRefund(false);
    }
  }; // handles submitting a refund request to the backend

  function useMediaQuery(query) {
    const [matches, setMatches] = useState(
      () => window.matchMedia(query).matches
    ); // initial value: check once on first render so the UI starts in the correct state

    useEffect(() => {
      const mql = window.matchMedia(query); // create a MediaQueryList object for this query (browser API)
      const handler = (e) => setMatches(e.matches); // handler runs whenever the viewport crosses the breakpoint (e.g. when resizing from <992px to >=992px)

      mql.addEventListener('change', handler); // subscribe to changes so 'matches' updates live as the screen size changes
      setMatches(mql.matches); // sync state immediately (useful if 'query' changes between renders)

      return () => mql.removeEventListener('change', handler); // cleanup: remove the event listener when the component unmounts or when 'query' changes, to prevent memory leaks / duplicate listeners
    }, [query]);

    return matches;
  } // custom hook: returns true/false depending on whether the CSS media query matches right now

  const showFullDate = useMediaQuery('(min-width: 992px)'); // true when viewport is at least 992px wide (Bootstrap "lg" breakpoint-ish)
  const showCustomer = useMediaQuery('(min-width: 768px)'); // true when viewport is at least 768px wide (Bootstrap "md" breakpoint-ish)

  return (
    <Container>
      <div>
        {/* Refund Modal */}
        <Modal
          className="refund-modal"
          show={showRefundModal}
          onHide={closeRefundModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Refund</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {refundError && (
              <div className="alert alert-danger">{refundError}</div>
            )}

            <div className="my-2">
              <strong>Order items</strong>
            </div>

            {orderItemsOfRefundOrder.map((i) => {
              const cfg = refundItems[i.key]; // the configuration of the current item, undefined until it is enabled (the inputs below render for enabled items only)
              const enabled = !!refundItems[i.key]; // check if the current item is enabled for refund

              return (
                <div key={i.key} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-start">
                      <div style={{ fontWeight: 600 }}>{i.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        {i.key !== 'shipping' &&
                          `Unit: ${formatEuro(i.unitGross)} • Ordered:
                            ${i.orderedQty} • `}
                        Total: {formatEuro(i.lineGross)}
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      label=""
                      checked={enabled}
                      onChange={(e) =>
                        e.target.checked
                          ? enableItem(i)
                          : clearItemRefund(i.key)
                      }
                      disabled={submittingRefund}
                    />
                  </div>

                  {enabled && (
                    <>
                      {/* the shipping line holds its full amount once from initialRefundLine and its inputs are disabled; the ternaries that repeated those values here went (Oct 2026, user decision) */}
                      <div className="d-flex gap-2 mt-2">
                        <Form.Select
                          style={{ width: 180 }}
                          value={cfg.refundType}
                          onChange={(e) =>
                            setItemRefund(i.key, { refundType: e.target.value })
                          }
                          disabled={submittingRefund || i.key === 'shipping'} // shipping locked
                        >
                          <option value="percent">Percent (%)</option>
                          <option value="amount">
                            Fixed amount (€) per unit
                          </option>
                        </Form.Select>

                        <Form.Control
                          inputMode="decimal"
                          value={cfg.refundValue}
                          onChange={(e) => {
                            setItemRefund(i.key, {
                              refundValue: normalizeRefundValue(
                                e.target.value,
                                cfg.refundType,
                                i.unitGross
                              ),
                            });
                          }}
                          placeholder={
                            cfg.refundType === 'percent'
                              ? 'e.g. 10'
                              : `max ${i.unitGross.toFixed(2)}`
                          }
                          disabled={submittingRefund || i.key === 'shipping'} // shipping locked
                        />

                        <Form.Control
                          type="number"
                          min={1}
                          max={i.orderedQty}
                          style={{ width: 120 }}
                          value={cfg.refundQty}
                          onChange={(e) =>
                            setItemRefund(i.key, {
                              refundQty: e.target.value,
                            })
                          }
                          disabled={submittingRefund || i.key === 'shipping'}
                          title={
                            i.key === 'shipping'
                              ? 'Shipping qty is always 1'
                              : ''
                          }
                        />
                      </div>

                      <Form.Group className="mt-2">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          value={cfg.note || ''}
                          onChange={(e) =>
                            setItemRefund(i.key, { note: e.target.value })
                          }
                          placeholder="Optional note for this item..."
                          disabled={submittingRefund}
                        />
                      </Form.Group>
                    </>
                  )}
                </div>
              );
            })}

            <div className="d-flex justify-content-between mt-3">
              <span>
                <strong>Total refund</strong>
              </span>
              <span>
                <strong>{refundAmount.toFixed(2)} €</strong>
              </span>
            </div>

            {exceedsOrderTotal && (
              <div
                className="alert alert-warning"
                style={{ fontSize: '14px', margin: '10px 0 0 0' }}
              >
                Total refund ({refundAmount.toFixed(2)} €) cannot exceed the
                order total ({maxRefund.toFixed(2)} €).
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeRefundModal}
              disabled={submittingRefund}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitRefund}
              disabled={!canSubmit || submittingRefund}
            >
              {submittingRefund ? 'Saving...' : 'Submit refund'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Order Details Modal */}
        <Modal
          className="address-modal"
          show={showShipModal}
          onHide={closeShipModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Order Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group
              controlId="shippingProvider"
              style={{ marginBottom: '15px' }}
            >
              <Form.Label style={{ fontWeight: '700' }}>
                Shipping Provider
              </Form.Label>

              <div className="d-flex justify-content-center">
                {SHIPPING_PROVIDERS.map((p) => (
                  <Form.Check
                    key={p}
                    type="radio"
                    name="shippingProvider" // important: same name = single selection
                    id={`shippingProvider-${p}`}
                    label={p}
                    value={p}
                    checked={shippingProvider === p}
                    onChange={(e) => setShippingProvider(e.target.value)}
                    className="mb-1 mx-5"
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group
              controlId="trackingNumber"
              style={{ marginBottom: '25px' }}
            >
              <Form.Label style={{ fontWeight: '700' }}>
                Tracking Number
              </Form.Label>
              <Form.Control
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number here..."
              />
            </Form.Group>

            {/* --- Shipping Boxes --- */}
            <hr />
            <div style={{ margin: '20px 0px' }}>
              <p
                className="mt-3"
                style={{ fontWeight: '700', fontSize: '14px' }}
              >
                Shipping Boxes (optional)
              </p>

              {manualShipBoxes.map((row, idx) => {
                const free = selectedFreeStock(shippingBoxes, row.id); // calculate the free stock of the current shipping box

                return (
                  <div
                    key={`sb-${idx}`}
                    className="d-flex gap-2 align-items-center mb-2"
                  >
                    <Form.Select
                      value={row.id}
                      onChange={(e) => {
                        const id = e.target.value;
                        setManualShipBoxes((prev) =>
                          prev.map((r, i) =>
                            i === idx ? { ...r, id, qty: 0 } : r
                          )
                        ); // reset the qty of the this shpipping box to 0, if the selected box changes
                      }}
                    >
                      <option value="">Select a shipping box…</option>
                      {shippingBoxes.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.name || b.size} — free: {freeStock(b)}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Control
                      style={{ maxWidth: 120 }}
                      type="number"
                      min={0}
                      step={1}
                      max={free}
                      value={row.qty}
                      onChange={(e) => {
                        const v = packagingQuantityFromInput(
                          e.target.value,
                          free
                        );
                        setManualShipBoxes((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, qty: v } : r))
                        ); // update the quantity of the shipping box
                      }}
                      placeholder="Qty"
                    />

                    <Button
                      variant="outline-secondary"
                      style={{ marginTop: '0px' }}
                      onClick={
                        () =>
                          setManualShipBoxes((prev) =>
                            prev.filter((_, i) => i !== idx)
                          ) // remove the shipping box
                      }
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}

              <Button
                size="sm"
                variant="link"
                onClick={
                  () =>
                    setManualShipBoxes((prev) => [...prev, { id: '', qty: 0 }]) // add a new shipping box with empty values
                }
              >
                Add Shipping Box
              </Button>
            </div>

            {/* --- Keycap Pullers --- */}
            <hr />
            <div style={{ margin: '20px 0px' }}>
              <p
                className="mt-3"
                style={{ fontWeight: '700', fontSize: '14px' }}
              >
                Keycap Pullers (optional)
              </p>

              {manualPullers.map((row, idx) => {
                const free = selectedFreeStock(keycapPullers, row.id); // calculate the free stock of the current keycap puller

                return (
                  <div
                    key={`kp-${idx}`}
                    className="d-flex gap-2 align-items-center mb-2"
                  >
                    <Form.Select
                      value={row.id}
                      onChange={(e) => {
                        const id = e.target.value;
                        setManualPullers(
                          (prev) =>
                            prev.map((r, i) =>
                              i === idx ? { ...r, id, qty: 0 } : r
                            ) // reset the qty of the this keycap puller to 0, if the selected keycap puller changes
                        );
                      }}
                    >
                      <option value="">Select a keycap puller…</option>
                      {keycapPullers.map((k) => (
                        <option key={k._id} value={k._id}>
                          {k.manufacturer} — free: {freeStock(k)}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Control
                      style={{ maxWidth: 120 }}
                      type="number"
                      min={0}
                      step={1}
                      max={free}
                      value={row.qty}
                      onChange={(e) => {
                        const v = packagingQuantityFromInput(
                          e.target.value,
                          free
                        );
                        setManualPullers((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, qty: v } : r))
                        ); // update the qty of the keycap puller
                      }}
                      placeholder="Qty"
                    />

                    <Button
                      variant="outline-secondary"
                      style={{ marginTop: '0px' }}
                      onClick={
                        () =>
                          setManualPullers((prev) =>
                            prev.filter((_, i) => i !== idx)
                          ) // remove the keycap puller
                      }
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}

              <Button
                size="sm"
                variant="link"
                onClick={
                  () =>
                    setManualPullers((prev) => [...prev, { id: '', qty: 0 }]) // add a new keycap puller with empty values
                }
              >
                Add Keycap Puller
              </Button>
            </div>

            {(itemsMissingCardboard.length > 0 ||
              itemsMissingFoam.length > 0) && (
              <>
                {/* Extra cardboard boxes */}
                {itemsMissingCardboard.length > 0 && (
                  <div>
                    <hr />
                    <p style={{ padding: '10px 0px 15px 0px' }}>
                      <strong>
                        Some keycap sets in this order have no cardboard box
                        assigned.
                      </strong>{' '}
                      Please add the cardboard boxes used for the following
                      items.{' '}
                      <span style={{ color: 'red' }}>
                        Do not include scrap.
                      </span>{' '}
                      Scrap must be added manually in stock.
                    </p>

                    <ul>
                      {itemsMissingCardboard.map((it) => (
                        <li key={it.orderItemId} style={{ fontWeight: '600' }}>
                          {it.productName || it.layoutName || '(no name)'}
                        </li>
                      ))}
                    </ul>

                    {extraCardboardRows.map((row, idx) => (
                      <Row key={idx} className="mb-2">
                        <Col md={7}>
                          <Form.Select
                            value={row.id || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setExtraCardboardRows((prev) =>
                                prev.map((r, i) =>
                                  i === idx ? { ...r, id: val } : r
                                )
                              );
                            }}
                          >
                            <option value="">Select cardboard box…</option>
                            {cardboardBoxes.map((box) => (
                              <option key={box._id} value={box._id}>
                                {box.size} (free: {freeStock(box)})
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            type="number"
                            min="1"
                            value={row.qty || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setExtraCardboardRows((prev) =>
                                prev.map((r, i) =>
                                  i === idx
                                    ? {
                                        ...r,
                                        qty: extraPackagingQuantityFromInput(
                                          val
                                        ),
                                      }
                                    : r
                                )
                              );
                            }}
                          />
                        </Col>
                        <Col md={2}>
                          <Button
                            variant="outline-secondary"
                            style={{ marginTop: '0px' }}
                            onClick={() =>
                              setExtraCardboardRows((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    <Button
                      size="sm"
                      variant="link"
                      onClick={() =>
                        setExtraCardboardRows((prev) => [
                          ...prev,
                          { id: '', qty: 1 },
                        ])
                      }
                    >
                      Add cardboard box
                    </Button>
                  </div>
                )}

                {/* Extra foams */}
                {itemsMissingFoam.length > 0 && (
                  <div>
                    <hr />
                    <p style={{ padding: '10px 0px 15px 0px' }}>
                      <strong>
                        Some keycap sets in this order have no foam assigned.
                      </strong>{' '}
                      Please add the foam used for the following items.{' '}
                      <span style={{ color: 'red' }}>
                        Do not include scrap.
                      </span>{' '}
                      Scrap must be added manually in stock.
                    </p>

                    <ul>
                      {itemsMissingFoam.map((it) => (
                        <li key={it.orderItemId} style={{ fontWeight: '600' }}>
                          {it.productName || it.layoutName || '(no name)'}
                        </li>
                      ))}
                    </ul>

                    {extraFoamRows.map((row, idx) => (
                      <Row key={idx} className="mb-2">
                        <Col md={7}>
                          <Form.Select
                            value={row.id || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setExtraFoamRows((prev) =>
                                prev.map((r, i) =>
                                  i === idx ? { ...r, id: val } : r
                                )
                              );
                            }}
                          >
                            <option value="">Select foam…</option>
                            {foams.map((foam) => (
                              <option key={foam._id} value={foam._id}>
                                {foam.size} {foam.type} (free: {freeStock(foam)}
                                )
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            type="number"
                            min="1"
                            value={row.qty || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setExtraFoamRows((prev) =>
                                prev.map((r, i) =>
                                  i === idx
                                    ? {
                                        ...r,
                                        qty: extraPackagingQuantityFromInput(
                                          val
                                        ),
                                      }
                                    : r
                                )
                              );
                            }}
                          />
                        </Col>
                        <Col
                          md={2}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <Button
                            variant="outline-secondary"
                            style={{ marginTop: '0px' }}
                            onClick={() =>
                              setExtraFoamRows((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    <Button
                      size="sm"
                      variant="link"
                      onClick={() =>
                        setExtraFoamRows((prev) => [
                          ...prev,
                          { id: '', qty: 1 },
                        ])
                      }
                    >
                      Add foam
                    </Button>
                  </div>
                )}
              </>
            )}

            {filmRows.length > 0 && (
              <>
                <hr />
                <p style={{ padding: '10px 0px 15px 0px' }}>
                  <strong>
                    This order includes items with transfer film pooling
                    enabled.
                  </strong>{' '}
                  Please enter the actual transfer film used for these items.{' '}
                  <span style={{ color: 'red' }}>Do not include scrap.</span>{' '}
                  Scrap must be added manually in stock.
                </p>
                {filmRows.map((row) => (
                  <Form.Group
                    className="mb-4"
                    key={row.orderItemId}
                    controlId={`film-${row.orderItemId}`}
                  >
                    <Form.Label style={{ fontWeight: '600' }}>
                      {row.title}{' '}
                      <small className="text-muted">
                        (reserved: {row.reservedQty})
                      </small>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={row.reservedQty}
                      value={filmInputs[row.orderItemId]} // every row's input is written with the rows (filmInputDefaults, one batched render), so the ?? 0 went (Oct 2026, user decision)
                      onChange={(e) => {
                        const n = usedFilmFromInput(
                          e.target.value,
                          row.reservedQty
                        );
                        setFilmInputs((prev) => ({
                          ...prev,
                          [row.orderItemId]: n,
                        }));
                      }}
                    />
                  </Form.Group>
                ))}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeShipModal}>
              Cancel
            </Button>
            {/* a second click while the request runs lands on the loading overlay (z-index 9999, above the modal's 1055), which loadingStatusUpdate shows from the first click on */}
            <Button
              variant="primary"
              onClick={async () => {
                if (!trackingNumber.trim()) {
                  toast.error('Tracking number is required');
                  return; // make sure the tracking number is provided, before continuing with submitting
                }

                const ok = await updateOrderStatus(shipOrderId, 'shipped', {
                  shipment: {
                    trackingNumber,
                    shippingProvider,
                    ...shipmentAdjustments({
                      filmRows,
                      filmInputs,
                      manualShipBoxes,
                      manualPullers,
                      extraCardboardRows,
                      extraFoamRows,
                    }),
                  },
                });
                if (ok) closeShipModal(); // a refused shipment keeps the dialog and its inputs (fixed Oct 2026)
              }}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        <div>
          <title>Orders</title>

          <h1 className="screen-heading">Orders</h1>

          <Nav
            justify
            variant="tabs"
            activeKey={filterParam}
            onSelect={(newFilterKey) => {
              setSearchInput('');
              navigate(`/admin/orders?filter=${newFilterKey}&page=1`);
            }}
            className="customizer-nav"
          >
            <Nav.Item>
              <Nav.Link eventKey="all">All</Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ position: 'relative' }}>
              {pendingOrders > 0 ? (
                <Badge className="notification-badge-nav">
                  {pendingOrders}
                </Badge>
              ) : null}
              <Nav.Link eventKey="pending">Pending</Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ position: 'relative' }}>
              {processingOrders > 0 ? (
                <Badge className="notification-badge-nav">
                  {processingOrders}
                </Badge>
              ) : null}
              <Nav.Link eventKey="processing">Processing</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="shipped">Shipped</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="delivered">Delivered</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="cancelled">Cancelled</Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Search Form */}
          <Form onSubmit={submitHandler} className="d-flex my-5">
            <Form.Control
              type="text"
              placeholder="Search by orderId, fullName, companyName, or userId"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search by orderId, fullName, companyName, or userId"
            />
            <Button
              type="submit"
              variant="secondary"
              style={{ marginLeft: '10px' }}
            >
              Search
            </Button>
          </Form>

          {loading || loadingStatusUpdate ? (
            <div className="overlay">
              <LoadingLogo />
            </div>
          ) : error ? (
            <MessageBox className="danger-alert">{error}</MessageBox>
          ) : (
            <div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    {showCustomer && <th>Customer</th>}
                    <th>Date</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const actions = orderActions(order); // which menu items the order gets

                    return (
                      <tr key={order.orderId}>
                        <td>{order.orderId}</td>

                        {showCustomer && (
                          <td
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              navigate(`/admin/user/${order.userId}`);
                            }}
                          >
                            {order.userId ? order.userId : 'DELETED USER'}
                          </td>
                        )}

                        <td>
                          {formatDate(order.createdAt, {
                            showTime: showFullDate,
                            showTimeZone: showFullDate,
                          })}
                        </td>

                        <td>{formatEuro(totalAfterRefund(order))}</td>

                        <td>
                          <Badge
                            bg={
                              order.status === 'pending' ||
                              order.status === 'processing'
                                ? 'primary'
                                : order.status === 'shipped' ||
                                    order.status === 'delivered'
                                  ? 'success'
                                  : order.status === 'cancelled'
                                    ? 'danger'
                                    : 'primary'
                            }
                          >
                            {order.status}
                          </Badge>
                        </td>
                        <td>
                          <DropdownButton
                            drop="down-centered"
                            variant="none"
                            className="no-css exclude-dropdown-arrow"
                            id={`order-actions-${order._id}`}
                            title={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="black"
                                className="bi bi-three-dots-vertical"
                                viewBox="0 0 16 16"
                              >
                                <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                              </svg>
                            }
                          >
                            <Dropdown.Item
                              onClick={() => {
                                navigate(`/order/${order.orderId}`);
                              }}
                            >
                              View details
                            </Dropdown.Item>

                            {actions.showProcessOrder && (
                              <Dropdown.Item
                                onClick={() =>
                                  updateOrderStatus(order._id, 'processing', {
                                    orderId: order.orderId,
                                  })
                                }
                              >
                                Process order
                              </Dropdown.Item>
                            )}

                            {actions.showContinueProcessing && (
                              <Dropdown.Item
                                onClick={() =>
                                  navigate(
                                    `/admin/process-order/${order.orderId}`
                                  )
                                }
                              >
                                Process order
                              </Dropdown.Item>
                            )}

                            {actions.showOrderShipped && (
                              <Dropdown.Item
                                onClick={() => openShipModal(order._id)}
                              >
                                Order shipped
                              </Dropdown.Item>
                            )}

                            {actions.showOrderDelivered && (
                              <Dropdown.Item
                                onClick={() =>
                                  updateOrderStatus(order._id, 'delivered')
                                }
                              >
                                Order delivered
                              </Dropdown.Item>
                            )}

                            {actions.showDangerSection && <Dropdown.Divider />}

                            {actions.showRefund && (
                              <Dropdown.Item
                                style={{ color: '#e74c3c' }}
                                onClick={() => openRefundModal(order)}
                              >
                                Refund order
                              </Dropdown.Item>
                            )}

                            {actions.showCancelOrder && (
                              <Dropdown.Item
                                style={{ color: '#e74c3c' }}
                                onClick={() => cancelOrder(order._id)}
                              >
                                Cancel order
                              </Dropdown.Item>
                            )}
                          </DropdownButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                pages={pages}
                page={Number(page)}
                filterParam={filterParam}
                baseUrl="/admin/orders"
                search={searchQuery}
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}
